export const partyHeaders: Record<string, string> = {
  Company: 'Company',
  FirstName: 'First Name',
  MiddleName: 'Middle Name',
  LastName: 'Last Name',
  Title: 'Title',
  Street: 'Street',
  City: 'City',
  State: 'State',
  Zip: 'Zip',
  LicenseNo: 'License No',
  CompanyLicenseNo: 'Company License No',
  BranchID: 'Branch ID',
  HomePhone: 'Home Phone',
  OtherPhone: 'Other Phone',
  Pager: 'Pager',
  WorkPhone: 'Work Phone',
  MobilePhone: 'Mobile Phone',
  Fax: 'Fax',
  EMail: 'Email',
  EMail2: 'Email 2',
  EMail3: 'Email 3',
  CompanyEIN: 'Company EIN',
  CompanyNMLSID: 'Company NMLS ID',
  ContactNMLSID: 'Contact NMLS ID',
  Notes: 'Notes',
}

export const tmoReports: Record<string, string> = {
  borrowerLateReport: 'BORROWER LATE REPORT',
  propertyInsuranceExpirationReport: 'PROPERTY INSURANCE EXPIRATION REPORT',
  lenderInvestmentProfileReport: 'LENDER INVESTMENT PROFILE REPORT',
  loanAgingTrialBalanceReport: 'LOAN AGING TRIAL BALANCE REPORT',
  loanBalancesJournalReport: 'LOAN BALANCES JOURNAL REPORT',
  loanInterestAccrualReport: 'LOAN INTEREST ACCRUAL REPORT',
  loanMasterReport: 'LOAN MASTER REPORT',
  loanMaturityReport: 'LOAN MATURITY REPORT',
  loanPaymentCodesSummaryReport: 'LOAN PAYMENT CODES SUMMARY REPORT',
  paymentRegister: 'PAYMENT REGISTER',
  borrowerPaymentDistributionAudit: 'BORROWER PAYMENT DISTRIBUTION AUDIT REPORT',
  paymentProjectionReport: 'PAYMENT PROJECTIONS REPORT',
  unpaidVouchersReport: 'UNPAID VOUCHERS REPORT',
}

// Trust-ledger reports. Kept separate from tmoReports so they render under their own heading
// on the servicing reports page.
export const trustLedgerReports: Record<string, string> = {
  loanTrustLedgerBalanceReport: 'LOAN TRUST LEDGER BALANCE REPORT',
  trustLedgerBucketBalancesReport: 'TRUST LEDGER BALANCES BY BUCKET REPORT',
  trustLedgerActivityReport: 'TRUST LEDGER ACTIVITY REPORT',
  trustLedgerReconciliationReport: 'TRUST LEDGER RECONCILIATION REPORT',
  trustLedgerSummaryByTypeReport: 'TRUST LEDGER SUMMARY BY TYPE REPORT',
  escrowSuspenseLedgerReport: 'ESCROW / IMPOUND & SUSPENSE LEDGER REPORT',
}

// Investor-facing reports. Kept separate from tmoReports so they render under their own heading
// on the servicing reports page.
export const investorReports: Record<string, string> = {
  investorStatement: 'INVESTOR STATEMENT',
}

// Regulatory filings. These are returns about the licensed entity rather than views of a selected
// book, so their options dialog offers a reporting quarter instead of the tag / as-of / servicing
// status filters every report above takes.
export const regulatoryReports: Record<string, string> = {
  mortgageCallReportRmla: 'MORTGAGE CALL REPORT - RMLA',
  mortgageCallReportFc: 'MORTGAGE CALL REPORT - FINANCIAL CONDITION',
}

// Cash-side reconciliation reports: what moved over a period and whether it ties to the bank.
// Grouped separately so the reconciliation set reads as one workflow on the servicing reports page.
export const cashReconciliationReports: Record<string, string> = {
  cashReceiptsJournal: 'CASH RECEIPTS JOURNAL',
  cashDisbursementsJournal: 'CASH DISBURSEMENTS JOURNAL',
  dailyCashProof: 'DAILY CASH PROOF',
  undepositedReceipts: 'UNDEPOSITED RECEIPTS / DEPOSITS IN TRANSIT',
  paymentReversalsNsfJournal: 'PAYMENT REVERSALS & NSF JOURNAL',
  payoutRunRegister: 'PAYOUT RUN REGISTER',
}

// Balance-side reconciliation reports: what is held, what is owed onward, what was earned, and
// finally everything the system can tell is out of balance.
export const balanceReconciliationReports: Record<string, string> = {
  suspenseUnappliedFunds: 'SUSPENSE & UNAPPLIED FUNDS',
  investorDistributionReconciliation: 'INVESTOR DISTRIBUTION RECONCILIATION',
  servicingIncomeSummary: 'SERVICING INCOME SUMMARY',
  reconciliationExceptions: 'RECONCILIATION EXCEPTIONS',
}

// Loan-charge reports. One rolls charges up by fee category across the book, the next stays
// loan-by-loan with the category breakdown inside each loan, and the last follows each charge
// through what has been collected against it. Grouped separately so they do not read as part of
// the reconciliation workflow.
export const chargeReports: Record<string, string> = {
  loanChargesByCategory: 'LOAN CHARGES BY CATEGORY',
  loanChargesSummaryReport: 'LOAN CHARGES SUMMARY REPORT',
  loanChargesHistoryReport: 'LOAN CHARGES HISTORY REPORT',
}

// Borrower-facing statements. One document per loan rather than a portfolio grid, so a run here
// is capped (see the Maximum Statements option) — scope it with tags or run it per loan from the
// loan's servicing Documents panel.
export const borrowerReports: Record<string, string> = {
  borrowerStatementOfAccount: 'BORROWER STATEMENT OF ACCOUNT',
}

/** Document-style servicing reports that cannot be flattened to a spreadsheet. */
export const PDF_ONLY_SERVICING_REPORTS = new Set<string>([
  tmoReports.loanMasterReport,
  investorReports.investorStatement,
  borrowerReports.borrowerStatementOfAccount,
])

export type ServicingReportDownloadFormat = 'pdf' | 'xlsx'
